<script setup>
import { computed, onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import EmailInboxFinish from './channels/emailChannels/EmailInboxFinish.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isATwilioWhatsAppChannel,
  isAVitalpbxChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const vitalpbxConfig = reactive({
  extensionNumber: '',
  deviceUser: '',
  phoneNumber: '',
  validationStatus: '',
  isLoading: false,
  isVerifying: false,
  verificationResult: null,
});

async function fetchVitalpbxDetails() {
  if (!isAVitalpbxChannel.value) return;
  vitalpbxConfig.isLoading = true;
  try {
    const { data } = await InboxesAPI.getVitalpbxConfiguration(
      route.params.inbox_id
    );
    vitalpbxConfig.extensionNumber =
      data.extension_number || currentInbox.value?.extension_number || '';
    vitalpbxConfig.deviceUser = data.device_user || '';
    vitalpbxConfig.phoneNumber =
      data.phone_number || currentInbox.value?.phone_number || '';
    vitalpbxConfig.amiUsername = data.ami_username || '';
    vitalpbxConfig.validationStatus = data.validation_status || '';
    if (data.validation_status === 'connected') {
      vitalpbxConfig.verificationResult = {
        success: true,
        message: t('INBOX_MGMT.FINISH.VITALPBX.STATUS_CONNECTED'),
      };
    }
  } catch (error) {
    vitalpbxConfig.extensionNumber = currentInbox.value?.extension_number || '';
    vitalpbxConfig.phoneNumber = currentInbox.value?.phone_number || '';
  } finally {
    vitalpbxConfig.isLoading = false;
  }
}

async function verifyVitalpbx() {
  vitalpbxConfig.isVerifying = true;
  vitalpbxConfig.verificationResult = null;
  try {
    const { data } = await InboxesAPI.verifyVitalpbxConnection(
      route.params.inbox_id
    );
    vitalpbxConfig.verificationResult = {
      success: true,
      message: data.message || t('INBOX_MGMT.FINISH.VITALPBX.VERIFIED_SUCCESS'),
    };
    vitalpbxConfig.validationStatus = 'connected';
  } catch (error) {
    vitalpbxConfig.verificationResult = {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('INBOX_MGMT.FINISH.VITALPBX.VERIFICATION_FAILED'),
    };
    vitalpbxConfig.validationStatus = 'error';
  } finally {
    vitalpbxConfig.isVerifying = false;
  }
}

const message = computed(() => {
  if (isAVitalpbxChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.VITALPBX.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WhatsApp (both Cloud and Twilio)
  if (currentInbox.value.phone_number && isAWhatsAppChannel.value) {
    // For Twilio WhatsApp, phone_number format is "whatsapp:+1234567890"
    // Extract just the phone number part for QR code generation
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

// Watch for currentInbox changes and regenerate QR codes when available
watch(
  currentInbox,
  newInbox => {
    if (newInbox) {
      generateQRCodes();
      fetchVitalpbxDetails();
    }
  },
  { immediate: true }
);

onMounted(() => {
  generateQRCodes();
  fetchVitalpbxDetails();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="isAnEmailChannel && !currentInbox.provider ? '' : message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioWhatsAppChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <EmailInboxFinish
          v-if="isAnEmailChannel && !currentInbox.provider"
          :inbox="currentInbox"
          :inbox-id="$route.params.inbox_id"
        />
        <div
          v-if="isAVitalpbxChannel"
          class="w-full max-w-2xl mx-auto text-left my-6"
        >
          <div
            class="p-6 bg-n-alpha-1 rounded-xl border border-n-strong space-y-4"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.FINISH.VITALPBX.SETUP_CHECKLIST_TITLE') }}
            </h3>

            <ol
              class="space-y-3 text-sm text-n-slate-12 list-decimal list-inside"
            >
              <li class="leading-relaxed">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_1_TITLE') }}
                </span>
                {{
                  $t('INBOX_MGMT.FINISH.VITALPBX.STEP_1_DESC', {
                    extension:
                      vitalpbxConfig.extensionNumber ||
                      currentInbox.extension_number ||
                      '2000',
                  })
                }}
              </li>
              <li class="leading-relaxed">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_2_TITLE') }}
                </span>
                {{
                  $t('INBOX_MGMT.FINISH.VITALPBX.STEP_2_DESC', {
                    user: vitalpbxConfig.deviceUser || 'chatwoot-extension',
                  })
                }}
              </li>
              <li class="leading-relaxed">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_3_TITLE') }}
                </span>
                {{
                  $t('INBOX_MGMT.FINISH.VITALPBX.STEP_3_DESC', {
                    phone:
                      vitalpbxConfig.phoneNumber ||
                      currentInbox.phone_number ||
                      $t('INBOX_MGMT.FINISH.VITALPBX.PHONE_FALLBACK'),
                    extension:
                      vitalpbxConfig.extensionNumber ||
                      currentInbox.extension_number ||
                      '2000',
                  })
                }}
              </li>
              <li class="leading-relaxed">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_4_TITLE') }}
                </span>
                {{
                  $t('INBOX_MGMT.FINISH.VITALPBX.STEP_4_DESC', {
                    user: vitalpbxConfig.amiUsername || 'chatwoot',
                  })
                }}
              </li>
              <li class="leading-relaxed">
                <span class="font-medium">
                  {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_5_TITLE') }}
                </span>
                {{ $t('INBOX_MGMT.FINISH.VITALPBX.STEP_5_DESC') }}
              </li>
            </ol>

            <div
              class="pt-3 flex items-center justify-between border-t border-n-strong"
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-block size-3 rounded-full"
                  :class="
                    vitalpbxConfig.validationStatus === 'connected'
                      ? 'bg-n-teal-9'
                      : 'bg-n-amber-9'
                  "
                />
                <span class="text-sm font-medium text-n-slate-11">
                  {{
                    vitalpbxConfig.validationStatus === 'connected'
                      ? $t('INBOX_MGMT.FINISH.VITALPBX.STATUS_CONNECTED')
                      : $t('INBOX_MGMT.FINISH.VITALPBX.STATUS_PENDING')
                  }}
                </span>
              </div>

              <NextButton
                outline
                slate
                size="sm"
                :is-loading="vitalpbxConfig.isVerifying"
                :label="$t('INBOX_MGMT.FINISH.VITALPBX.VERIFY_BUTTON')"
                @click="verifyVitalpbx"
              />
            </div>

            <div
              v-if="vitalpbxConfig.verificationResult"
              class="p-3 rounded-lg text-sm"
              :class="
                vitalpbxConfig.verificationResult.success
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
            >
              {{ vitalpbxConfig.verificationResult.message }}
            </div>
          </div>
        </div>
        <div
          v-if="isAWhatsAppChannel && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              alt="WhatsApp QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div class="flex gap-2 justify-center mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
